import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts';
import { statusFor } from '../../lib/ai';

const STATUS_STYLES = {
  ok:   { ring: 'ring-emerald-500/30', glow: 'shadow-[0_0_28px_-4px_rgba(16,185,129,0.45)]', dot: 'bg-emerald-500',  text: 'text-emerald-600 dark:text-emerald-400', stroke: '#10b981' },
  warn: { ring: 'ring-amber-500/30',   glow: 'shadow-[0_0_28px_-4px_rgba(245,158,11,0.45)]',  dot: 'bg-amber-500',    text: 'text-amber-600 dark:text-amber-400',     stroke: '#f59e0b' },
  bad:  { ring: 'ring-red-500/30',     glow: 'shadow-[0_0_28px_-4px_rgba(239,68,68,0.55)]',   dot: 'bg-red-500',      text: 'text-red-600 dark:text-red-400',         stroke: '#ef4444' }
};

const ICON_GRADIENTS = {
  indigo:  'linear-gradient(135deg,#6366f1,#4f46e5)',
  sky:     'linear-gradient(135deg,#0ea5e9,#0284c7)',
  rose:    'linear-gradient(135deg,#f43f5e,#e11d48)',
  emerald: 'linear-gradient(135deg,#10b981,#059669)',
  amber:   'linear-gradient(135deg,#f59e0b,#d97706)',
  violet:  'linear-gradient(135deg,#8b5cf6,#7c3aed)',
  fuchsia: 'linear-gradient(135deg,#d946ef,#c026d3)'
};

export default function SensorCard({
  title, metric, value, unit, icon: Icon, color = 'indigo',
  formatter = (v) => Number(v).toFixed(1),
  threshold
}) {
  const [history, setHistory] = useState([]);
  const lastValRef = useRef(value);

  // Append to local sparkline history on each change
  useEffect(() => {
    setHistory(h => {
      const next = [...h, { v: Number.isFinite(value) ? value : 0 }];
      return next.slice(-30);
    });
    lastValRef.current = value;
  }, [value]);

  const status = statusFor(metric, value);
  const s = STATUS_STYLES[status] || STATUS_STYLES.ok;

  return (
    <motion.div
      layout
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.985 }}
      className={`relative overflow-hidden card-surface ring-1 ${s.ring} ${s.glow} p-4 sm:p-5 transition-shadow`}
    >
      {/* Top row */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-2xl grid place-items-center text-white shadow-glow"
            style={{ backgroundImage: ICON_GRADIENTS[color] || ICON_GRADIENTS.indigo }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider font-bold opacity-60">{title}</p>
            <div className="flex items-baseline gap-1.5">
              <motion.span
                key={String(value)}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl sm:text-3xl font-extrabold tracking-tight tabular-nums"
              >
                {formatter(value)}
              </motion.span>
              <span className="text-xs font-semibold opacity-60">{unit}</span>
            </div>
          </div>
        </div>

        <div className={`chip ${s.text} bg-black/[0.03] dark:bg-white/[0.04]`}>
          <span className={`w-1.5 h-1.5 rounded-full ${s.dot} animate-pulse-soft`} />
          {status === 'ok' ? 'Normal' : status === 'warn' ? 'Warn' : 'Alert'}
        </div>
      </div>

      {/* Mini sparkline */}
      <div className="mt-3 h-12 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={history}>
            <defs>
              <linearGradient id={`grad-${metric}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"  stopColor={s.stroke} stopOpacity={0.1} />
                <stop offset="100%" stopColor={s.stroke} stopOpacity={1} />
              </linearGradient>
            </defs>
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Line
              type="monotone"
              dataKey="v"
              stroke={`url(#grad-${metric})`}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {threshold && (
        <div className="mt-2 flex items-center justify-between text-[11px] opacity-60 font-medium">
          <span>Safe ≤ {threshold.safe}{unit}</span>
          <span>Crit ≥ {threshold.crit}{unit}</span>
        </div>
      )}
    </motion.div>
  );
}
